/**
 * Local Document Sync State Registry (Protocol v1)
 *
 * Tracks locally known document heads, writer counters, and workspace cursors.
 */

export interface LocalDocSyncRecord {
  documentId: string;
  workspaceId: string;
  version?: number;
  head?: string;
  stateFingerprint?: string;
  writerCounter?: number;
  serverUpdatedAt: string;
  syncedAt: number;
}

const DB_NAME = 'yada-sync-state';
const DB_VERSION = 2;
const STORE_NAME = 'doc_sync_state';
const CURSOR_STORE = 'workspace_cursors';

const inMemorySyncMap = new Map<string, LocalDocSyncRecord>();
const inMemoryCursors = new Map<string, string>();
let isLoadedFromIDB = false;
let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'documentId' });
        store.createIndex('workspaceId', 'workspaceId', { unique: false });
      }
      if (!db.objectStoreNames.contains(CURSOR_STORE)) {
        db.createObjectStore(CURSOR_STORE, { keyPath: 'workspaceId' });
      }
    };
  });

  return dbPromise;
}

/**
 * Record a document's synced version/head and server updated timestamp.
 */
export async function recordLocalDocSync(
  documentId: string,
  workspaceId: string,
  headOrVersion: string | number,
  serverUpdatedAt?: string,
  writerCounter?: number,
  stateFingerprint?: string
): Promise<void> {
  const existing = inMemorySyncMap.get(documentId);
  const head = typeof headOrVersion === 'string' ? headOrVersion : `c_${headOrVersion}`;
  const version = typeof headOrVersion === 'number' ? headOrVersion : existing?.version ?? 0;

  const record: LocalDocSyncRecord = {
    documentId,
    workspaceId,
    version,
    head,
    stateFingerprint,
    writerCounter: Math.max(writerCounter ?? 0, existing?.writerCounter ?? 0),
    serverUpdatedAt: serverUpdatedAt || new Date().toISOString(),
    syncedAt: Date.now(),
  };

  inMemorySyncMap.set(documentId, record);

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(documentId);
      req.onsuccess = () => {
        record.writerCounter = Math.max(record.writerCounter ?? 0, req.result?.writerCounter ?? 0);
        store.put(record);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = tx.onabort = () => reject(tx.error);
    });
  } catch {
    // Non-fatal
  }
}

/**
 * Get next monotonically increasing counter for a writer on a document.
 */
export async function getNextWriterCounter(documentId: string, floor = 0): Promise<number> {
  // Reserve before sending. A repeated (writer, counter) is acknowledged by the
  // node as an idempotent retry, even if the new payload contains different data.
  const existing = await getLocalDocSync(documentId);
  const reserve = (stored?: LocalDocSyncRecord): LocalDocSyncRecord => ({
    documentId,
    workspaceId: stored?.workspaceId ?? existing?.workspaceId ?? '',
    ...stored,
    writerCounter: Math.max(stored?.writerCounter ?? 0,
      inMemorySyncMap.get(documentId)?.writerCounter ?? 0, floor) + 1,
    serverUpdatedAt: stored?.serverUpdatedAt ?? '',
    syncedAt: stored?.syncedAt ?? 0,
  });
  if (typeof indexedDB === 'undefined') {
    const record = reserve(existing);
    inMemorySyncMap.set(documentId, record);
    return record.writerCounter!;
  }
  const db = await openDB();
  return new Promise((resolve, reject) => {
    // A read/write transaction serializes reservations across browser tabs too.
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    let record: LocalDocSyncRecord;
    const request = store.get(documentId);
    request.onsuccess = () => {
      record = reserve(request.result);
      store.put(record);
    };
    tx.oncomplete = () => {
      inMemorySyncMap.set(documentId, record);
      resolve(record.writerCounter!);
    };
    tx.onerror = tx.onabort = () => reject(tx.error ?? new Error('Counter reservation failed'));
  });
}

/**
 * Get sync record for a specific document.
 */
export async function getLocalDocSync(documentId: string): Promise<LocalDocSyncRecord | undefined> {
  if (inMemorySyncMap.has(documentId)) {
    return inMemorySyncMap.get(documentId);
  }

  try {
    const db = await openDB();
    const record = await new Promise<LocalDocSyncRecord | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(documentId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });

    if (record) {
      inMemorySyncMap.set(documentId, record);
    }
    return record;
  } catch {
    return inMemorySyncMap.get(documentId);
  }
}

/**
 * Record the latest feed cursor seen for a workspace.
 */
export async function recordWorkspaceCursor(workspaceId: string, cursor: string): Promise<void> {
  inMemoryCursors.set(workspaceId, cursor);
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(CURSOR_STORE, 'readwrite');
      const store = tx.objectStore(CURSOR_STORE);
      const req = store.put({ workspaceId, cursor, updatedAt: Date.now() });
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Non-fatal
  }
}

/**
 * Get the latest feed cursor seen for a workspace.
 */
export async function getWorkspaceCursor(workspaceId: string): Promise<string | undefined> {
  if (inMemoryCursors.has(workspaceId)) {
    return inMemoryCursors.get(workspaceId);
  }
  try {
    const db = await openDB();
    const record = await new Promise<{ workspaceId: string; cursor: string } | undefined>((resolve, reject) => {
      const tx = db.transaction(CURSOR_STORE, 'readonly');
      const store = tx.objectStore(CURSOR_STORE);
      const req = store.get(workspaceId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (record) {
      inMemoryCursors.set(workspaceId, record.cursor);
      return record.cursor;
    }
  } catch {
    // Ignore
  }
  return undefined;
}

/**
 * Load all local sync records.
 */
export async function getAllLocalDocSync(): Promise<Map<string, LocalDocSyncRecord>> {
  if (isLoadedFromIDB) {
    return new Map(inMemorySyncMap);
  }

  try {
    const db = await openDB();
    const records = await new Promise<LocalDocSyncRecord[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });

    for (const record of records) {
      inMemorySyncMap.set(record.documentId, record);
    }
    isLoadedFromIDB = true;
  } catch {
    // Return what we have in memory
  }

  return new Map(inMemorySyncMap);
}

export async function deleteLocalDocSync(documentId: string): Promise<void> {
  inMemorySyncMap.delete(documentId);
  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(documentId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // Non-fatal
  }
}

export async function clearAllLocalDocSync(): Promise<void> {
  inMemorySyncMap.clear();
  inMemoryCursors.clear();
  isLoadedFromIDB = false;

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_NAME, CURSOR_STORE], 'readwrite');
      tx.objectStore(STORE_NAME).clear();
      tx.objectStore(CURSOR_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    // Without IndexedDB nothing was persisted; any other failure left records behind.
    if (typeof indexedDB !== 'undefined') throw err;
  }
}
